import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { SessionService } from "./session.service";

export type ReportingMode = "classic" | "apex";

/**
 * ClassicModeService — global "Clio Classic ⇄ Clio Apex" mode toggle (ClioAdmin #71, Phase 1).
 *
 * Lets customers trained on the V1 MedStatix dashboard opt into a V1-faithful
 * reporting experience inside the V2 SPA. The mode is persisted in localStorage
 * under `clio_reporting_mode`.
 *
 * CRITICAL: the default is ALWAYS `'apex'`. Existing users who have never
 * touched the toggle must keep the Apex nav. We never default to `'classic'`
 * or `undefined`.
 */
@Injectable({ providedIn: "root" })
export class ClassicModeService {
    public static readonly STORAGE_KEY = "clio_reporting_mode";

    private modeSubject: BehaviorSubject<ReportingMode>;
    public mode$;

    constructor(private sessionService: SessionService) {
        this.modeSubject = new BehaviorSubject<ReportingMode>(this.readPersistedMode());
        this.mode$ = this.modeSubject.asObservable();
    }

    /** Current reporting mode. */
    get mode(): ReportingMode {
        return this.modeSubject.value;
    }

    /** Set the reporting mode and persist it. */
    setMode(mode: ReportingMode): void {
        const next: ReportingMode = mode === "classic" ? "classic" : "apex";
        try {
            localStorage.setItem(ClassicModeService.STORAGE_KEY, next);
        } catch {
            // localStorage unavailable (private mode / quota) — in-memory only.
        }
        this.modeSubject.next(next);
    }

    /** Flip between classic and apex. */
    toggle(): void {
        this.setMode(this.mode === "classic" ? "apex" : "classic");
    }

    /**
     * Whether the Classic experience is available to the current user.
     *
     * Reads the `dashboardModules` claim off the session user, replicating the
     * `msxinfo` → `msxInfo` lowercase fallback that `getDashboardModules` in
     * pagelayout.component.ts uses. Returns true when the modules array
     * includes `'classicReports'` OR the modules array is absent (fail open),
     * matching the existing convention so the feature is testable locally
     * before clio-server mints the claim.
     */
    isClassicAvailable(): boolean {
        const attributes = this.sessionService.currentUser?.attributes;
        if (!attributes) {
            return false;
        }
        // Replicate the lowercase-fallback normalization used elsewhere.
        if (attributes.msxinfo !== undefined && attributes.msxInfo === undefined) {
            attributes.msxInfo = attributes.msxinfo;
        }
        const modules = attributes.msxInfo?.dashboardModules;
        if (modules === undefined) {
            // Fail open — no claim minted yet.
            return true;
        }
        return Array.isArray(modules) && modules.includes("classicReports");
    }

    /** Read the persisted mode, defaulting to 'apex' for any unset/invalid value. */
    private readPersistedMode(): ReportingMode {
        try {
            return localStorage.getItem(ClassicModeService.STORAGE_KEY) === "classic" ? "classic" : "apex";
        } catch {
            return "apex";
        }
    }
}
